// frontend/src/pages/WorkshopQuotationsPage.js
// Devis the workshop has issued — create/edit at the top, list below.
// Chrome/ink, French. Row actions depend on status: a pending devis can be
// edited or confirmed on the client's behalf; a revised one can be accepted,
// countered or refused.

import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import WorkshopQuotationInvoiceForm from "../components/WorkshopQuotationInvoiceForm";
import WorkshopQuotationDetail from "../components/WorkshopQuotationDetail";
import API_URL from '../api/baseURL';
import {
  Page, Panel, Badge, Table, Tr, Td, Button, Modal, Field,
  EmptyState, Loading, ErrorNote, inputClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

const STATUS = {
  pending:   { label: 'En attente',   tone: 'warn' },
  revised:   { label: 'Révisé',       tone: 'warn' },
  countered: { label: 'Contre-offre', tone: 'warn' },
  accepted:  { label: 'Accepté',      tone: 'ok' },
  confirmed: { label: 'Confirmé',     tone: 'ok' },
  rejected:  { label: 'Refusé',       tone: 'danger' },
};
const statusOf = (s) => STATUS[s] || { label: s || '—', tone: 'neutral' };

export default function WorkshopQuotationsPage({ token: propToken }) {
  const { token: contextToken } = useAuth();
  const token = propToken || contextToken;

  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState('all');
  const [loadingIds, setLoadingIds] = useState({}); // loading state per row
  const [editing, setEditing] = useState(null);     // quotation being edited (pending)
  const [viewing, setViewing] = useState(null);     // quotation being viewed (any status)
  const [showForm, setShowForm] = useState(false);
  // Negotiation state, per quotation id
  const [negotiating, setNegotiating] = useState(null);
  const [counterOffer, setCounterOffer] = useState('');
  const [acceptPrice, setAcceptPrice] = useState('');

  // Download the quotation as a PDF so the workshop can send it manually
  // (e.g. via WhatsApp) when SMS/email delivery is unavailable.
  const handleDownloadPdf = async (q) => {
    try {
      const res = await fetch(`${API_URL}/workshop-quotations/${q._id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Échec du téléchargement du PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Devis-${q.documentNumber || q._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Erreur lors du téléchargement du PDF");
    }
  };

  useEffect(() => {
    if (!token) {
      setError("Session expirée — reconnectez-vous.");
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const [cl, ve, qu] = await Promise.all([
          fetch(`${API_URL}/users?role=car owner`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (!res.ok) throw new Error("Échec du chargement des clients");
            return res.json();
          }),
          fetch(`${API_URL}/vehicles/assigned`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (!res.ok) throw new Error("Échec du chargement des véhicules");
            return res.json();
          }),
          fetch(`${API_URL}/workshop-quotations/quotation/list`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (!res.ok) throw new Error("Échec du chargement des devis");
            return res.json();
          }),
        ]);
        setClients(cl);
        setVehicles(ve);
        setQuotations(qu);
        setError("");
      } catch (err) {
        setError(err.message || "Chargement impossible");
        setClients([]);
        setVehicles([]);
        setQuotations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, refresh]);

  const handleRefresh = () => setRefresh(x => !x);

  // The workshop may confirm a devis the client agreed to in person or by
  // phone. It goes through the same /decision endpoint as a client acceptance,
  // so the invoice is created identically — the backend records that the
  // workshop was the confirming party, and the client is notified.
  const handleWorkshopConfirm = async (q) => {
    const label = q.documentNumber || 'ce devis';
    if (!window.confirm(
      `Confirmer ${label} au nom du client ?\n\n` +
      `Une facture sera créée immédiatement et le client sera notifié que ` +
      `l'atelier a confirmé à sa place.`
    )) return;

    setLoadingIds((prev) => ({ ...prev, [q._id]: true }));
    try {
      const res = await fetch(`${API_URL}/workshop-quotations/${q._id}/decision`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'accepted' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Échec de la confirmation');
      }
      handleRefresh();
    } catch (e) {
      setError(e.message || 'Échec de la confirmation');
    } finally {
      setLoadingIds((prev) => ({ ...prev, [q._id]: false }));
    }
  };

  const handleAction = async (q, actionType) => {
    setLoadingIds((prev) => ({ ...prev, [q._id]: true }));
    setError('');
    try {
      const payload = {
        action: actionType,
        workshopResponseNote: q.workshopResponseNote || "",
      };
      if (actionType === "counter") {
        payload.counterOffer = Number(counterOffer);
        if (!payload.counterOffer || payload.counterOffer <= 0) {
          setLoadingIds((prev) => ({ ...prev, [q._id]: false }));
          return setError("Saisissez un montant de contre-offre valide.");
        }
      }
      if (actionType === "accept" && acceptPrice && Number(acceptPrice) > 0) {
        payload.finalPrice = Number(acceptPrice);
      }
      const res = await fetch(
        `${API_URL}/workshop-quotations/${q._id}/workshop-action`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || e.message || "Échec de la mise à jour du devis");
      }
      setNegotiating(null);
      setCounterOffer('');
      setAcceptPrice('');
      handleRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingIds((prev) => ({ ...prev, [q._id]: false }));
    }
  };

  const counts = useMemo(() => ({
    all: quotations.length,
    pending: quotations.filter(q => ['pending', 'revised', 'countered'].includes(q.status)).length,
    settled: quotations.filter(q => ['accepted', 'confirmed'].includes(q.status)).length,
  }), [quotations]);

  const shown = useMemo(() => {
    if (filter === 'pending') return quotations.filter(q => ['pending', 'revised', 'countered'].includes(q.status));
    if (filter === 'settled') return quotations.filter(q => ['accepted', 'confirmed'].includes(q.status));
    return quotations;
  }, [quotations, filter]);

  const chips = [
    { key: 'all', label: `Tous · ${counts.all}` },
    { key: 'pending', label: `En attente · ${counts.pending}` },
    { key: 'settled', label: `Acceptés · ${counts.settled}` },
  ];

  const formOpen = showForm || !!editing;

  return (
    <Page
      title="Devis"
      subtitle="Atelier"
      actions={
        <>
          <Button onClick={handleRefresh}>Actualiser</Button>
          <Button
            variant="primary"
            onClick={() => { setEditing(null); setShowForm((v) => !v); }}
          >
            {formOpen ? 'Fermer le formulaire' : 'Nouveau devis'}
          </Button>
        </>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      {formOpen && (
        <WorkshopQuotationInvoiceForm
          type="quotation"
          token={token}
          clients={clients}
          vehicles={vehicles}
          editQuotation={editing}
          onCancelEdit={() => { setEditing(null); setShowForm(false); }}
          onSubmitted={() => { setEditing(null); setShowForm(false); handleRefresh(); }}
        />
      )}

      <Panel
        title="Devis envoyés"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {chips.map(c => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  filter === c.key ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <Loading label="Chargement des devis…" />
        ) : quotations.length === 0 ? (
          <EmptyState
            title="Aucun devis"
            hint="Créez un devis pour un client : il le recevra dans son espace et pourra l'accepter en ligne."
            action={<Button variant="primary" onClick={() => setShowForm(true)}>Nouveau devis</Button>}
          />
        ) : shown.length === 0 ? (
          <EmptyState title="Aucun devis dans cette vue." />
        ) : (
          <Table head={['Client', 'Véhicule', 'Total', 'Statut', 'Note client', '']} minWidth={940}>
            {shown.map((q) => {
              const st = statusOf(q.status);
              const busy = !!loadingIds[q._id];
              return (
                <Tr key={q._id}>
                  <Td className="text-[#F4F1EA]">
                    {q.client?.name || '—'}
                    {q.documentNumber && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5 font-['Space_Grotesk',sans-serif]">
                        {q.documentNumber}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                      {q.vehicle?.plateNumber || '—'}
                    </span>
                    {/* The reading the devis was quoted at, not the car's
                        current one — that is the number under discussion when
                        a job is argued about later. */}
                    {q.odometerReading > 0 && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5 font-['Space_Grotesk',sans-serif] tabular-nums">
                        {fmt(q.odometerReading)} km
                      </span>
                    )}
                  </Td>
                  <Td mono>
                    {fmt(q.clientTotal || q.total || 0)}{' '}
                    <span className="text-[10.5px] text-[#6E665C]">F</span>
                  </Td>
                  <Td>
                    <Badge tone={st.tone}>{st.label}</Badge>
                    {q.partiallyAccepted && (
                      <span className="block text-[10px] text-[#DFA23C] mt-1">Partiel</span>
                    )}
                  </Td>
                  <Td className="max-w-[200px] truncate text-[#8F877B]">{q.clientNote || '—'}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button variant="quiet" className="h-[26px] px-2" onClick={() => setViewing(q)}>
                      Voir
                    </Button>
                    <Button variant="quiet" className="h-[26px] px-2" onClick={() => handleDownloadPdf(q)}>
                      PDF
                    </Button>
                    {q.status === 'pending' && (
                      <>
                        <Button
                          variant="quiet" className="h-[26px] px-2"
                          onClick={() => { setEditing(q); setShowForm(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        >
                          Modifier
                        </Button>
                        <Button
                          className="h-[26px] px-2.5 text-[11px] ml-1"
                          disabled={busy}
                          title="Le client a accepté sur place ou par téléphone"
                          onClick={() => handleWorkshopConfirm(q)}
                        >
                          {busy ? '…' : 'Confirmer'}
                        </Button>
                      </>
                    )}
                    {['revised', 'countered'].includes(q.status) && (
                      <Button
                        className="h-[26px] px-2.5 text-[11px] ml-1"
                        disabled={busy}
                        onClick={() => { setNegotiating(q); setCounterOffer(q.counterOffer || ''); setAcceptPrice(''); }}
                      >
                        Répondre
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>

      {viewing && (
        <WorkshopQuotationDetail quotation={viewing} onClose={() => setViewing(null)} />
      )}

      {/* Negotiation: the client asked for a revision, the workshop answers. */}
      <Modal
        open={!!negotiating}
        onClose={() => setNegotiating(null)}
        subtitle={negotiating?.documentNumber || 'Devis'}
        title="Répondre au client"
        footer={
          <>
            <Button
              variant="danger"
              disabled={loadingIds[negotiating?._id]}
              onClick={() => handleAction(negotiating, 'reject')}
            >
              Refuser
            </Button>
            <Button
              disabled={loadingIds[negotiating?._id] || !counterOffer}
              onClick={() => handleAction(negotiating, 'counter')}
            >
              Contre-offre
            </Button>
            <Button
              variant="primary"
              disabled={loadingIds[negotiating?._id]}
              onClick={() => handleAction(negotiating, 'accept')}
            >
              Accepter
            </Button>
          </>
        }
      >
        {negotiating?.clientNote && (
          <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
            <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1.5">Note du client</p>
            <p className="text-[12.5px] text-[#C9C3B6] leading-[1.55]">{negotiating.clientNote}</p>
          </div>
        )}
        <Field label="Prix convenu (optionnel)" hint="Laissez vide pour garder le total du devis.">
          <input
            type="number" min="0" value={acceptPrice}
            onChange={(e) => setAcceptPrice(e.target.value)}
            className={inputClass} placeholder={fmt(negotiating?.clientTotal || negotiating?.total || 0)}
          />
        </Field>
        <Field label="Contre-offre" hint="Requis pour envoyer une contre-offre.">
          <input
            type="number" min="0" value={counterOffer}
            onChange={(e) => setCounterOffer(e.target.value)}
            className={inputClass} placeholder="0"
          />
        </Field>
      </Modal>
    </Page>
  );
}
